import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';

/** The zone the server computes "today" in (APP_TIMEZONE) — the clock shows the same wall time. */
export const APP_TIME_ZONE = 'Asia/Bangkok';

const RESYNC_MS = 10 * 60_000;

export interface ServerClock {
  /** Current time, corrected by the measured server offset. */
  now: Date;
  /** Server minus local clock in ms; null until a sync succeeds (the local clock is shown meanwhile). */
  offsetMs: number | null;
}

/**
 * Measures how far this computer's clock is from the server's, using the `Date` header of
 * GET /health and the request's round trip (NTP-style midpoint). `Date` has one-second
 * resolution, so the result is good to about ±0.5 s plus half the round trip. `Date` is not a
 * CORS-safelisted header, so the API lists it in ExposedHeaders for cross-origin dev setups;
 * if it still cannot be read, the local clock is shown.
 */
async function measureOffset(): Promise<number | null> {
  const t0 = Date.now();
  const res = await apiClient.get('/health');
  const t1 = Date.now();
  const header = res.headers['date'] as string | undefined;
  const server = header ? Date.parse(header) : NaN;
  if (Number.isNaN(server)) return null;
  // The header is truncated to the second: assume the server read its clock mid-second.
  return server + 500 - (t0 + t1) / 2;
}

/** A clock that ticks exactly on each second boundary of the (server-corrected) time. */
export function useServerClock(): ServerClock {
  const [offsetMs, setOffsetMs] = useState<number | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      measureOffset()
        .then((o) => {
          if (!cancelled && o !== null) setOffsetMs(o);
        })
        .catch(() => {
          // Keep the last good offset (or the local clock) if the server is unreachable.
        });
    };
    sync();
    const id = setInterval(sync, RESYNC_MS);
    // Background tabs throttle timers and laptops sleep; resync when the tab is seen again.
    const onVisible = () => document.visibilityState === 'visible' && sync();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const t = Date.now() + (offsetMs ?? 0);
      setNow(new Date(t));
      // Re-arm for the next whole second each time, so the display never drifts.
      timer = setTimeout(tick, 1000 - (t % 1000) + 5);
    };
    tick();
    return () => clearTimeout(timer);
  }, [offsetMs]);

  return { now, offsetMs };
}
